import {
  forwardRef,
  InputHTMLAttributes,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
  ReactNode,
} from "react";
import cn from "clsx";

const CONTROL =
  "w-full px-3 py-2 text-sm bg-surface border border-border rounded-md text-text " +
  "placeholder:text-text-3 focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500 " +
  "disabled:opacity-50 disabled:cursor-not-allowed";

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** Optional: kennzeichnet das Feld als optional (rendert „(optional)"). */
  optional?: boolean;
  children: ReactNode;
  className?: string;
}

/** Label + Control-Wrapper mit Hint/Error (Spec §3). */
export function Field({ label, hint, error, optional, children, className }: FieldProps) {
  return (
    <div className={cn("space-y-1.5", className)}>
      {label && (
        <label className="text-xs font-medium text-text mb-1.5 flex items-center gap-1">
          {label}
          {optional && <span className="text-text-3 font-normal">(optional)</span>}
        </label>
      )}
      {children}
      {error ? (
        <p className="text-xs text-danger">{error}</p>
      ) : hint ? (
        <p className="text-xs text-text-3">{hint}</p>
      ) : null}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={cn(CONTROL, className)} {...rest} />;
  }
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cn(CONTROL, "resize-none", className)} {...rest} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <select ref={ref} className={cn(CONTROL, className)} {...rest}>
        {children}
      </select>
    );
  }
);

export default Field;
